import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getBarcode,
  getPoItems,
  getTargetVsActualConfigForWorkflowStage,
  getTimelineData,
  getVehicleTrackingDetail,
  getVehicleTrackingLineItems,
  moveToNextStage,
  skipStage,
} from 'src/redux/Actions/VehicleTracking';
import { PrintBarcodeComponent } from 'src/utils/PrintBarcodeComponent';
import { decodeUuidFromBase64 } from 'src/utils/DecodeBase64Function';
import {
  getLoadLineItemsForVehicleTracking,
  getVehicleTrackingAttachedLoads,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';

export default function useHeaderSectionLogic() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workflowStageId, vehicleTrackingNumber } = useParams();

  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;
  const themeColor = authReducer.userData.tenant?.theme;
  const userName = authReducer.userData.username;
  const isOrganisationAdmin = authReducer.organisationAdmin;

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const {
    vehicleTrackingDetails,
    vehicleTrackingLineItemList,
    targetVsActualConfig,
  } = vehicleTrackingDataReducer;

  const { vehicleTrackingAttachedLoads, loadLineItemsForVehicleTracking } =
    VehicleTrackingOutwardReducer;

  const [showCameraWeightReport, setShowCameraWeightReport] = useState(false);
  const [showTargetVsActualAlert, setShowTargetVsActualAlert] = useState(false);
  const [showVehicleContentAlert, setShowVehicleContentAlert] = useState(false);
  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);
  const [actionButtonAnimation, setActionButtonAnimation] = useState(true);
  const [targetVsActualConfigLoaded, setTargetVsActualConfigLoaded] =
    useState(false);
  const [showEmptyLoadAlert, setShowEmptyLoadAlert] = useState(false);
  const [showEmptyManageLoadAlert, setShowEmptyManageLoadAlert] =
    useState(false);
  const [showIssueStampTableAlert, setShowIssueStampTableAlert] =
    useState(false);
  const [
    showCaptureWeightForOutwardAlert,
    setShowCaptureWeightForOutwardAlert,
  ] = useState(false);
  const { hasPermission } = usePermissions();

  const hasResumeVehicleTrackingPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.RESUME_VEHICLE_TRACKING,
  );

  const hasCreateVehicleTrackingEnterLogPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE_VEHICLE_TRACKING_ENTRY_LOG,
  );

  const hasCreateVehicleTrackingExitLogPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE_VEHICLE_TRACKING_EXIT_LOG,
  );

  const hasCreateLoadHeaderPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE_LOAD_HEADER,
  );

  const hasCreateTargetVsActualPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.OUTWARD.CREATE_TARGET_VS_ACTUAL,
  );

  const hasUpdateVehicleTrackingCancelStatusPermission = hasPermission(
    ACTIONS.VEHICLE_TRACKING.EDIT_CANCEL_STATUS,
  );

  const moveToNextStageSubmissionLockRef = useRef(false);

  useEffect(() => {
    dispatch(
      getVehicleTrackingDetail(
        vehicleTrackingNumber,
        organisationId,
        workflowStageId,
      ),
    )
      .then(async () => {
        const results = await Promise.allSettled([
          dispatch(
            getPoItems(
              vehicleTrackingNumber,
              [],
              organisationId,
              workflowStageId,
            ),
          ),
          dispatch(
            getTargetVsActualConfigForWorkflowStage(
              workflowStageId,
              vehicleTrackingNumber,
            ),
          ),
          dispatch(
            getVehicleTrackingLineItems(organisationId, vehicleTrackingNumber),
          ),
          dispatch(getTimelineData(vehicleTrackingNumber, organisationId)),
        ]);
        // Below, we are checking the success or failure of the getTargetVsActualConfigForWorkflowStage API.
        // Based on the status, we are setting the state to true or false, since the result status is
        // either 'fulfilled' or 'rejected' from Promise.allSettled.
        const targetVsActualResult = results[1];
        setTargetVsActualConfigLoaded(
          targetVsActualResult.status === 'fulfilled',
        );
      })
      .catch(() => {
        // redirect user to stations page if vehicle tracking details not found
        navigate('/stations/');
      });
  }, [dispatch, vehicleTrackingNumber, organisationId, workflowStageId]);

  useEffect(() => {
    const animationTimeout = setTimeout(
      () => setActionButtonAnimation(false),
      8000,
    );
    return () => clearTimeout(animationTimeout);
  }, []);

  const direction = vehicleTrackingDetails?.direction?.id;
  const isOnHold = vehicleTrackingDetails?.isOnHold;

  useEffect(() => {
    if (direction === 2) {
      dispatch(
        getVehicleTrackingAttachedLoads(organisationId, vehicleTrackingNumber),
      );
    }
  }, [dispatch, direction, organisationId, vehicleTrackingNumber]);

  useEffect(() => {
    if (direction === 2) {
      dispatch(
        getLoadLineItemsForVehicleTracking(
          organisationId,
          vehicleTrackingNumber,
        ),
      );
    }
  }, [dispatch, direction, organisationId, vehicleTrackingNumber]);

  const requestedStage =
    vehicleTrackingDataReducer?.vehicleTrackingDetails?.requestedStage;

  const vehicleTrackingStatus =
    vehicleTrackingDataReducer?.vehicleTrackingDetails?.vehicleTrackingStatus;

  const logType =
    requestedStage?.entry_log_exist && !requestedStage?.completed ? '2' : '1';

  const isTargetVsActualPending = targetVsActualConfig.some(
    (item) => !item.completed,
  );

  const vehicleTrackingLoadData =
    vehicleTrackingAttachedLoads?.vehicleTrackingLoadData;

  const allowLoadTransactionEnable =
    vehicleTrackingDetails?.requestedStage?.allow_load_transaction;

  const allowWeightCapture =
    vehicleTrackingDetails?.requestedStage?.allow_weight_capture;

  const areAllUnlockedLoadsInStatusThree = () => {
    // Filter only unlocked loads
    const unlockedLoads = vehicleTrackingLoadData?.filter(
      (load) => load.is_locked === false,
    );
    // Check if all unlocked loads have status id = 3
    return unlockedLoads?.every((load) => load.status?.id === 3);
  };

  const isEveryUnlockedLoadInStatusThree = areAllUnlockedLoadsInStatusThree();

  // for capture weight station stage
  const areAllUnlockedLoadsCompleted = () => {
    // Confirmed loads = loads that have reached the "Gross Weight" stage
    const confirmedLoads = vehicleTrackingLoadData?.filter(
      (load) => load.status?.id === 3,
    );

    // Every confirmed load must have its gross weight actually captured
    return confirmedLoads?.every((load) => load.actual_gross_weight > 0);
  };

  const isEveryUnlockedLoadCompleted = areAllUnlockedLoadsCompleted();

  const isEntryLogExist = requestedStage?.entry_log_exist;

  const hasEwayBillAndInvoiceNumber =
    loadLineItemsForVehicleTracking?.hasEwayBillAndInvoiceNumber;

  const allowIssueStampAddition =
    vehicleTrackingDetails?.requestedStage?.allow_issue_stamp_addition;

  const isCancelled = Number(vehicleTrackingStatus?.id) === 5;

  useEffect(() => {
    let buffer = '',
      timeout;
    const handleKeyPress = (event) => {
      if (timeout) clearTimeout(timeout);
      if (event.key === 'Unidentified') {
        // device sent Unidentified skip it to avoid
        // sending Unidentified in L203
        return;
      }
      if (event.key === 'Enter') {
        if (buffer) {
          buffer = buffer.replace(/Shift/g, '');
          handleMoveToNextStageWithoutTargetVsActualSubmit(buffer, true);
          buffer = '';
        }
      } else {
        buffer += event.key;
      }
      timeout = setTimeout(() => {
        buffer = '';
      }, 500);
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    workflowStageId,
    logType,
    vehicleTrackingNumber,
    targetVsActualConfig,
    vehicleTrackingDataReducer,
  ]);

  const handlePrint = () => {
    dispatch(getBarcode(organisationId, vehicleTrackingNumber)).then(
      (barcodeData) => {
        PrintBarcodeComponent(barcodeData, userName);
      },
    );
  };

  const handleSkipStage = () => {
    dispatch(
      skipStage(
        {
          vehicleTrackingNumber,
          workflowStageId: requestedStage?.workflow_stage_id,
        },
        organisationId,
      ),
    ).then(() => {
      navigate('/stations/');
    });
  };

  const handleMoveToNextStageWithoutTargetVsActualSubmit = (
    scannedVehicleId,
    isScanned,
  ) => {
    if (moveToNextStageSubmissionLockRef.current) return;
    moveToNextStageSubmissionLockRef.current = true;

    const currentStageId = currentWorkflowStageId
      ? String(currentWorkflowStageId)
      : '';
    const nextStageId = nextWorkflowStageId ? String(nextWorkflowStageId) : '';

    if (
      currentStageId !== workflowStageId &&
      (currentStageId !== '' || nextStageId !== workflowStageId)
    ) {
      moveToNextStageSubmissionLockRef.current = false;
      return;
    }
    if (logType === '2' && isTargetVsActualPending) {
      setShowTargetVsActualAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else if (
      !isCancelled &&
      logType === '2' &&
      vehicleTrackingLineItemList?.length === 0 &&
      requestedStage.workflow_stage_name === 'Logistics' &&
      direction === 1 // for inward vehicles
    ) {
      setShowVehicleContentAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else if (
      !isCancelled &&
      direction === 2 &&
      requestedStage?.allow_vehicle_content_additon &&
      isEntryLogExist &&
      vehicleTrackingLoadData?.length <= 0
    ) {
      // show error when direction is outward and user does not created even a single load
      setShowEmptyLoadAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else if (
      !isCancelled &&
      direction === 2 &&
      isEntryLogExist &&
      allowLoadTransactionEnable &&
      allowWeightCapture === false &&
      !isEveryUnlockedLoadInStatusThree
    ) {
      // for outward vehicle i.e for loading station
      // when allowLoadTransactionEnable is enable and user does not managed even a single load
      setShowEmptyManageLoadAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else if (
      !isCancelled &&
      direction === 2 &&
      isEntryLogExist &&
      allowLoadTransactionEnable &&
      allowWeightCapture &&
      !isEveryUnlockedLoadCompleted
    ) {
      // for outward vehicle i.e for capture weight station
      // when allowLoadTransactionEnable is true and allowWeightCapture is true and user does not captured weight for even a single load
      setShowCaptureWeightForOutwardAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else if (
      !isCancelled &&
      direction === 2 &&
      isEntryLogExist &&
      allowIssueStampAddition &&
      !hasEwayBillAndInvoiceNumber
    ) {
      // for outward vehicle
      // when allowIssueStampAddition is enable and user does not filled even a single row
      setShowIssueStampTableAlert(true);
      moveToNextStageSubmissionLockRef.current = false;
    } else {
      const requestData = {
        tenant_organisation_id: organisationId,
        vehicle_tracking_number: vehicleTrackingNumber,
        workflow_stage_id: workflowStageId,
        log_type: logType,
        target_vs_actual: null,
      };

      const isVehicleAuthorized = isScanned
        ? decodeUuidFromBase64(scannedVehicleId) === vehicleTrackingNumber
        : true;

      if (isVehicleAuthorized) {
        dispatch(moveToNextStage(requestData))
          .then(async () => {
            moveToNextStageSubmissionLockRef.current = false;

            if (
              requestData.log_type === '1' ||
              requestedStage.allow_next_stage_selection
            ) {
              const results = await Promise.allSettled([
                dispatch(
                  getVehicleTrackingDetail(
                    vehicleTrackingNumber,
                    organisationId,
                    workflowStageId,
                  ),
                ),
                dispatch(
                  getPoItems(
                    vehicleTrackingNumber,
                    [],
                    organisationId,
                    workflowStageId,
                  ),
                ),
                dispatch(
                  getVehicleTrackingLineItems(
                    organisationId,
                    vehicleTrackingNumber,
                  ),
                ),
                dispatch(
                  getTimelineData(vehicleTrackingNumber, organisationId),
                ),
                dispatch(
                  getTargetVsActualConfigForWorkflowStage(
                    workflowStageId,
                    vehicleTrackingNumber,
                  ),
                )
                  .then(() => {
                    setTargetVsActualConfigLoaded(true);
                  })
                  .catch(() => setTargetVsActualConfigLoaded(false)),
              ]);
              // when getVehicleTrackingDetail fails i.e Vehicle tracking details not found navigate user to stations
              if (results[0].status === 'rejected') {
                navigate('/stations/');
              }
            } else {
              navigate(`/stations/`);
            }
          })
          .catch(() => {
            moveToNextStageSubmissionLockRef.current = false;
          });
      } else {
        moveToNextStageSubmissionLockRef.current = false;
      }
    }
  };

  const navigateToReport = () => navigate(`/report/${vehicleTrackingNumber}`);
  const vehicleNumber = vehicleTrackingDetails?.vehicleNumber;
  const vehicleTrackingCode = vehicleTrackingDetails?.vehicleTrackingCode;
  const currentWorkflowStageId = vehicleTrackingDetails?.currentWorkflowStageId;
  const isVehicleVerified = vehicleTrackingDetails?.isVehicleVerified;
  const nextWorkflowStageId = vehicleTrackingDetails?.nextWorkflowStageId;
  const requestedStageExit = requestedStage?.exit_log_exist;
  const requestedStageEntry = requestedStage?.entry_log_exist;

  const showVehicleDataAddButton =
    requestedStage?.allow_vehicle_content_additon &&
    vehicleTrackingDetails?.direction?.id === 1 &&
    !requestedStage?.completed &&
    requestedStageEntry;

  const showCreateLoadButton =
    requestedStage?.allow_vehicle_content_additon &&
    vehicleTrackingDetails?.direction?.id === 2 &&
    !requestedStage?.completed &&
    requestedStageEntry &&
    hasCreateLoadHeaderPermission;

  const showResumeVehicleTrackingButton =
    direction === 2 && isOnHold && hasResumeVehicleTrackingPermission;

  const markExit = !requestedStageExit && requestedStageEntry;

  function showEnterExitButton() {
    const currentStageId = currentWorkflowStageId
      ? String(currentWorkflowStageId)
      : '';
    const nextStageId = nextWorkflowStageId ? String(nextWorkflowStageId) : '';
    if (
      currentStageId === workflowStageId ||
      (currentStageId === '' && nextStageId === workflowStageId)
    )
      return true;
    return false;
  }

  function shouldRenderGrnComponent() {
    return (
      requestedStage?.allow_grn_sync &&
      requestedStageEntry &&
      !vehicleTrackingDetails?.isGrnCompleted
    );
  }

  function shouldShowInvoiceUploadButton() {
    // Show invoice upload button if user can add vehicle content (logistics stations)
    return (
      showVehicleDataAddButton &&
      requestedStageEntry &&
      !requestedStage?.completed
    );
  }

  const toggleInvoiceUploadModal = () => {
    setShowInvoiceUploadModal((prev) => !prev);
  };

  return {
    themeColor,
    vehicleNumber,
    vehicleTrackingCode,
    requestedStage,
    requestedStageEntry,
    requestedStageExit,
    isOnHold,
    showCameraWeightReport,
    setShowCameraWeightReport,
    showTargetVsActualAlert,
    setShowTargetVsActualAlert,
    showVehicleContentAlert,
    setShowVehicleContentAlert,
    showInvoiceUploadModal,
    setShowInvoiceUploadModal,
    handlePrint,
    handleSkipStage,
    handleMoveToNextStageWithoutTargetVsActualSubmit,
    navigateToReport,
    showVehicleDataAddButton,
    showCreateLoadButton,
    showResumeVehicleTrackingButton,
    markExit,
    isOrganisationAdmin,
    actionButtonAnimation,
    vehicleTrackingLineItemList,
    targetVsActualConfig,
    shouldRenderGrnComponent,
    shouldShowInvoiceUploadButton,
    toggleInvoiceUploadModal,
    showEnterExitButton,
    targetVsActualConfigLoaded,
    setShowEmptyLoadAlert,
    showEmptyLoadAlert,
    showEmptyManageLoadAlert,
    setShowEmptyManageLoadAlert,
    showIssueStampTableAlert,
    setShowIssueStampTableAlert,
    showCaptureWeightForOutwardAlert,
    setShowCaptureWeightForOutwardAlert,
    hasCreateVehicleTrackingEnterLogPermission,
    hasCreateVehicleTrackingExitLogPermission,
    hasCreateTargetVsActualPermission,
    direction,
    isVehicleVerified,
    vehicleTrackingStatus,
    hasUpdateVehicleTrackingCancelStatusPermission,
  };
}
